import React, { useState } from 'react';
import styled from 'styled-components';
import { Sparkles, Loader, AlertCircle, X } from 'lucide-react';
import { generateSimulation } from '../utils/simulationApi';
import { API_URL } from '../config';

const SimulationSection = styled.div`
  background: white;
  border-radius: 20px;
  padding: 2rem;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  margin-top: 2rem;
  animation: fadeIn 0.6s ease-out;
  
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const SectionHeader = styled.div`
  margin-bottom: 1.25rem;
`;

const SectionTitle = styled.h3`
  font-size: 1.75rem;
  font-weight: 800;
  color: #0f172a;
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  letter-spacing: -0.02em;
`;

const SectionSubtitle = styled.p`
  color: #64748b;
  font-size: 1rem;
  line-height: 1.6;
  font-weight: 400;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  font-weight: 600;
  color: #0f172a;
  margin-bottom: 0.75rem;
  font-size: 1rem;
  letter-spacing: -0.01em;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 1rem;
  font-family: inherit;
  resize: vertical;
  background: #fafbfc;
  color: #0f172a;
  transition: all 0.3s ease;
  line-height: 1.6;
  
  &:focus {
    outline: none;
    border-color: #3a5cf7;
    background: white;
    box-shadow: 0 0 0 4px rgba(58, 92, 247, 0.1);
  }
  
  &::placeholder {
    color: #94a3b8;
  }
`;

const ToggleGroup = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1rem;
`;

const ToggleButton = styled.button`
  flex: 1;
  padding: 0.875rem 1.25rem;
  border-radius: 12px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 2px solid ${props => props.active ? '#3a5cf7' : '#e2e8f0'};
  background: ${props => props.active 
    ? 'linear-gradient(135deg, #3a5cf7 0%, #2d4ae8 100%)' 
    : 'white'};
  color: ${props => props.active ? 'white' : '#475569'};
  box-shadow: ${props => props.active 
    ? '0 4px 6px -1px rgba(58, 92, 247, 0.3)' 
    : '0 1px 2px rgba(0, 0, 0, 0.05)'};
  
  &:hover {
    border-color: ${props => props.active ? '#2d4ae8' : '#cbd5e1'};
    background: ${props => props.active 
      ? 'linear-gradient(135deg, #2d4ae8 0%, #1e3a8a 100%)' 
      : '#f8fafc'};
    transform: translateY(-2px);
    box-shadow: ${props => props.active 
      ? '0 8px 12px -2px rgba(58, 92, 247, 0.4)' 
      : '0 4px 6px -1px rgba(0, 0, 0, 0.1)'};
  }
`;

const SliderContainer = styled.div`
  margin-top: 0.5rem;
`;

const Slider = styled.input`
  width: 100%;
  height: 8px;
  border-radius: 4px;
  background: #e5e7eb;
  outline: none;
  -webkit-appearance: none;
  
  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #3a5cf7;
    cursor: pointer;
    transition: all 0.2s;
    
    &:hover {
      background: #2d4ae8;
      transform: scale(1.1);
    }
  }
  
  &::-moz-range-thumb {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #3a5cf7;
    cursor: pointer;
    border: none;
    transition: all 0.2s;
    
    &:hover {
      background: #2d4ae8;
      transform: scale(1.1);
    }
  }
`;

const SliderValue = styled.div`
  text-align: center;
  margin-top: 0.75rem;
  font-size: 1rem;
  color: #475569;
  font-weight: 600;
  padding: 0.5rem 1rem;
  background: linear-gradient(135deg, #eff6ff 0%, #e0e7ff 100%);
  border-radius: 8px;
  display: inline-block;
  border: 1px solid rgba(58, 92, 247, 0.2);
`;

const GenerateButton = styled.button`
  width: 100%;
  padding: 1.125rem;
  border-radius: 14px;
  font-weight: 600;
  font-size: 1.0625rem;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: none;
  background: linear-gradient(135deg, #3a5cf7 0%, #2d4ae8 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  box-shadow: 0 4px 6px -1px rgba(58, 92, 247, 0.3), 0 2px 4px -1px rgba(58, 92, 247, 0.2);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s;
  }
  
  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #2d4ae8 0%, #1e3a8a 100%);
    transform: translateY(-3px);
    box-shadow: 0 12px 24px -4px rgba(58, 92, 247, 0.4);
    
    &::before {
      left: 100%;
    }
  }
  
  &:active:not(:disabled) {
    transform: translateY(-1px);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none !important;
  }
`;

const ErrorBanner = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  padding: 1rem;
  margin-top: 1rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: #dc2626;
`;

const ResultSection = styled.div`
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid #e5e7eb;
`;

const ComparisonContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  margin-top: 1.5rem;
  animation: fadeInUp 0.6s ease-out 0.2s both;
  
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const ImageCard = styled.div`
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 16px;
  padding: 1.5rem;
  border: 2px solid #e2e8f0;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
    border-color: rgba(58, 92, 247, 0.3);
  }
`;

const ImageLabel = styled.div`
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 1rem;
  font-size: 1.0625rem;
  letter-spacing: -0.01em;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &::before {
    content: '';
    width: 4px;
    height: 20px;
    background: linear-gradient(180deg, #3a5cf7, #8b5cf6);
    border-radius: 2px;
  }
`;

const ResultImage = styled.img`
  width: 100%;
  height: auto;
  border-radius: 12px;
  display: block;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
  border: 2px solid white;
  
  &:hover {
    transform: scale(1.03);
  }
`;

const MetaTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 1rem;
`;

const Tag = styled.span`
  padding: 0.5rem 1rem;
  background: linear-gradient(135deg, #eff6ff 0%, #e0e7ff 100%);
  border-radius: 8px;
  font-size: 0.875rem;
  color: #3a5cf7;
  font-weight: 600;
  border: 1px solid rgba(58, 92, 247, 0.2);
  box-shadow: 0 1px 2px rgba(58, 92, 247, 0.1);
`;

const LoadingOverlay = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  color: #6b7280;
  gap: 1rem;
`;

function NoseSimulation({ imageUrl, onImageGenerated }) {
  const [userIntent, setUserIntent] = useState('');
  const [styleMode, setStyleMode] = useState('natural');
  const [intensity, setIntensity] = useState(0.4);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  // Convert relative URL to full URL for image display
  const getFullImageUrl = (url) => {
    if (!url) return url;
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    const baseUrl = API_URL || '';
    return baseUrl + url;
  };

  const handleModeChange = (mode) => {
    setStyleMode(mode);
    // Adjust intensity based on mode
    if (mode === 'natural') {
      setIntensity(0.4);
    } else {
      setIntensity(0.8);
    }
  };

  const handleGenerate = async () => {
    if (!userIntent.trim()) {
      setError('Please describe how you\'d like your nose to look.');
      return;
    }

    if (!imageUrl) {
      setError('No image available. Please upload a photo first.');
      return;
    }

    setIsGenerating(true);
    setError(null);
    setResult(null);

    try {
      // Analytics disabled - tracking removed

      const response = await generateSimulation({
        imageUrl,
        userIntent: userIntent.trim(),
        styleMode,
        intensity,
      });

      if (response.success) {
        setResult(response);
        if (onImageGenerated) {
          onImageGenerated(response.afterImageUrl);
        }
      } else {
        setError(response.error || 'Failed to generate simulation. Please try again.');
      }
    } catch (err) {
      console.error('Simulation error:', err);
      setError(err.message || 'We couldn\'t generate your "after" image. Please tweak your description or try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const formatIntent = (intent) => {
    if (intent.length > 50) {
      return intent.substring(0, 50) + '...';
    }
    return intent;
  };

  return (
    <SimulationSection>
      <SectionHeader>
        <SectionTitle>
          <Sparkles size={24} />
          Describe your ideal nose
        </SectionTitle>
        <SectionSubtitle>
          Tell AI how you'd like your nose to look. We'll generate a personalized 'after' image based on your preferences.
        </SectionSubtitle>
      </SectionHeader>

      <FormGroup>
        <Label htmlFor="nose-intent">Your description</Label>
        <TextArea
          id="nose-intent"
          value={userIntent}
          onChange={(e) => setUserIntent(e.target.value)}
          placeholder="Slightly slimmer bridge, natural."
          disabled={isGenerating}
        />
      </FormGroup>

      <FormGroup>
        <Label>Style preference</Label>
        <ToggleGroup>
          <ToggleButton
            active={styleMode === 'natural'}
            onClick={() => handleModeChange('natural')}
            disabled={isGenerating}
          >
            Keep it natural
          </ToggleButton>
          <ToggleButton
            active={styleMode === 'glam'}
            onClick={() => handleModeChange('glam')}
            disabled={isGenerating}
          >
            Go full glam
          </ToggleButton>
        </ToggleGroup>
      </FormGroup>

      <FormGroup>
        <Label>Transformation intensity</Label>
        <SliderContainer>
          <Slider
            type="range"
            min="0.2"
            max="1.0"
            step="0.1"
            value={intensity}
            onChange={(e) => setIntensity(parseFloat(e.target.value))}
            disabled={isGenerating}
          />
          <SliderValue>{intensity.toFixed(1)} / 1.0</SliderValue>
        </SliderContainer>
      </FormGroup>

      <GenerateButton
        onClick={handleGenerate}
        disabled={isGenerating || !userIntent.trim()}
      >
        {isGenerating ? (
          <>
            <Loader size={20} className="animate-spin" />
            Generating...
          </>
        ) : (
          <>
            <Sparkles size={20} />
            Generate my AI 'after'
          </>
        )}
      </GenerateButton>

      {error && (
        <ErrorBanner>
          <AlertCircle size={20} />
          <span>{error}</span>
          <button
            onClick={() => setError(null)}
            style={{
              marginLeft: 'auto',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#991b1b',
            }}
          >
            <X size={18} />
          </button>
        </ErrorBanner>
      )}

      {isGenerating && (
        <LoadingOverlay>
          <Loader size={32} className="animate-spin" />
          <p>Creating your personalized simulation...</p>
        </LoadingOverlay>
      )}

      {result && !isGenerating && (
        <ResultSection>
          <ComparisonContainer>
            <ImageCard>
              <ImageLabel>Before</ImageLabel>
              <ResultImage src={getFullImageUrl(imageUrl)} alt="Before" />
            </ImageCard>
            <ImageCard>
              <ImageLabel>Your AI 'After'</ImageLabel>
              <ResultImage src={getFullImageUrl(result.afterImageUrl)} alt="After" />
            </ImageCard>
          </ComparisonContainer>
          <MetaTags>
            <Tag>Intent: {formatIntent(userIntent)}</Tag>
            <Tag>Mode: {styleMode === 'natural' ? 'Natural' : 'Glam'}</Tag>
            <Tag>Intensity: {intensity.toFixed(1)}/1.0</Tag>
          </MetaTags>
        </ResultSection>
      )}
    </SimulationSection>
  );
}

export default NoseSimulation;

